/**
 * The side menu, as data. Pure, so which items a role set sees is tested
 * rather than eyeballed.
 *
 * Hiding an item is a courtesy, not a control: every route behind one is
 * guarded again by `RequireRole` in the browser and by `withRole` in the API.
 * The menu exists so a person is not shown doors they cannot open.
 *
 * Every top-level item names an icon (D-084), because the rail collapses to a
 * strip of them; a sub-item folds into its parent when it does. Settings is
 * not in the rail: it is the gear in the header bar (D-086), as Jobber's.
 */

import type { Role } from '@unitedos/shared';
import type { IconName } from '../components/Icon.js';

export interface NavItem {
  readonly to: string;
  readonly label: string;
  /** The icon the collapsed rail shows; every top-level item has one. */
  readonly icon?: IconName;
  /** Visible to everyone signed in when absent. */
  readonly requires?: Role;
  /** Match only the exact path (the home link), not every path beneath it. */
  readonly end?: boolean;
  /** Drawn indented beneath the item above it (a sub-page of that item). */
  readonly sub?: boolean;
  /** Paths this item also lights for — its sub-items' — while the rail is collapsed and they are folded into it. */
  readonly alsoActive?: readonly string[];
  /** Not built yet: the shell draws it as a muted label with a "Coming soon" mark, not a link (Dakota, 2026-10-05). */
  readonly soon?: boolean;
}

export interface NavSection {
  readonly title?: string;
  readonly items: readonly NavItem[];
}

/**
 * Two sections, a rule between them, as Jobber's rail: the day (Home, Schedule), then the
 * workflow in Jobber's order with Jobs first because it is the one that exists. The other three
 * are placeholders (`soon`) at the addresses they will take, so the rail already shows the shape
 * of the whole product.
 */
export const NAV: readonly NavSection[] = [
  {
    items: [
      { to: '/', label: 'Home', icon: 'home', end: true },
      { to: '/to-dos', label: 'To-dos', icon: 'list-checks' },
      { to: '/schedule', label: 'Schedule', icon: 'calendar' },
      // Meetings (design 19, D-100): one entry; a person's own issues and to-dos are each tab's Mine tick (MeetingsTasks §8).
      { to: '/meetings', label: 'Meetings', icon: 'users', alsoActive: ['/issues', '/live-meetings', '/legacy-meetings'] },
    ],
  },
  {
    items: [
      { to: '/jobs', label: 'Jobs', icon: 'wrench' },
      // Quotes (design 27): Jobber's quotes over the mirror and the office's worksheet, between Jobs and Purchase orders as Jobber orders them.
      { to: '/quotes', label: 'Quotes', icon: 'quote' },
      // Purchase orders (design 19, D-104): the warehouse's POs against a job's line items, under Jobs; Costs & POs since design 47 (D-300), the address kept.
      { to: '/purchase-orders', label: 'Costs & POs', icon: 'package' },
      // Billing (design 20, D-113; every job in billing, GC and PM as tabs, D-115): the GC Jobs sheet as a list, under Jobs.
      { to: '/billing', label: 'Billing', icon: 'receipt' },
      { to: '/requests', label: 'Requests', icon: 'inbox', soon: true },
      { to: '/invoices', label: 'Invoices', icon: 'invoice', soon: true },
    ],
  },
];

/** The header bar's gear (D-086): Settings, for admins. */
export const SETTINGS_LINK: NavItem = { to: '/settings', label: 'Settings', icon: 'settings', requires: 'admin' };

export function canSee(item: NavItem, roles: readonly Role[]): boolean {
  return item.requires === undefined || roles.includes(item.requires);
}

/** The sections a person with these roles sees, with empty sections removed. */
export function navFor(roles: readonly Role[], nav: readonly NavSection[] = NAV): readonly NavSection[] {
  return nav
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => canSee(item, roles)),
    }))
    .filter((section) => section.items.length > 0);
}

/** The same sections with every sub-item folded into its parent: what the collapsed rail draws. */
export function foldSubItems(sections: readonly NavSection[]): readonly NavSection[] {
  return sections.map((section) => ({ ...section, items: section.items.filter((item) => !item.sub) })).filter((section) => section.items.length > 0);
}
